import { PrintLayout, PrintWidth } from '@/@types/print';
import { useAuth } from '@/contexts/AuthContext';
import { useGetCashSessionSummary } from '@/modules/cash_management/hooks';
import {
  PRINT_LAYOUT_1,
  PRINT_WIDTH_80MM,
} from '@/modules/utils/print-layouts';
import {
  dispatchPrintIntent,
  isAndroidDevice,
} from '@/modules/utils/print-intent';
import { parseErrorMessage } from '@/utils';
import { useSnackbar } from 'notistack';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { CashSessionSummaryPdfPrint } from './CashSessionSummaryPdfPrint';
import { CashSessionSummaryPosPrint } from './CashSessionSummaryPosPrint';

export type CashSessionSummaryPrintMode = 'pdf' | 'pos';
export type CashSessionSummaryPrintMethod = 'browser' | 'intent';

type PrintRequest = {
  copies: number;
  id: string;
  mode: CashSessionSummaryPrintMode;
  method: CashSessionSummaryPrintMethod;
  printWidth: PrintWidth;
  selectedLayout: PrintLayout;
};

type UseCashSessionSummaryPrintResult = {
  isPrintingSummary: boolean;
  printSummaryNode: ReactNode;
  printCashSessionSummary: (
    // eslint-disable-next-line no-unused-vars
    id: string,
    // eslint-disable-next-line no-unused-vars
    mode: CashSessionSummaryPrintMode,
    // eslint-disable-next-line no-unused-vars
    copies?: number,
    // eslint-disable-next-line no-unused-vars
    selectedLayout?: PrintLayout,
    // eslint-disable-next-line no-unused-vars
    printWidth?: PrintWidth,
    // eslint-disable-next-line no-unused-vars
    method?: CashSessionSummaryPrintMethod
  ) => void;
};

export function useCashSessionSummaryPrint(): UseCashSessionSummaryPrintResult {
  const [printRequest, setPrintRequest] = useState<PrintRequest>();
  const pdfPrintRef = useRef<HTMLDivElement | null>(null);
  const posPrintRef = useRef<HTMLDivElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const summaryQuery = useGetCashSessionSummary({
    id: printRequest?.id,
    enabled: !!printRequest,
    onError: (error: unknown) => {
      enqueueSnackbar(
        parseErrorMessage(error, 'Não foi possível carregar o resumo do caixa'),
        {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        }
      );
      setPrintRequest(undefined);
    },
  });

  const handleAfterPrint = () => {
    setPrintRequest((currentRequest) => {
      if (!currentRequest) return undefined;

      if (currentRequest.copies <= 1) return undefined;

      return { ...currentRequest, copies: currentRequest.copies - 1 };
    });
  };

  const handlePrintPdf = useReactToPrint({
    content: () => pdfPrintRef.current,
    onAfterPrint: handleAfterPrint,
  });

  const handlePrintPos = useReactToPrint({
    content: () => posPrintRef.current,
    onAfterPrint: handleAfterPrint,
  });

  useEffect(() => {
    if (!printRequest || summaryQuery.isFetching) return;
    if (summaryQuery.data?.cashSession.id !== printRequest.id) return;

    if (printRequest.mode === 'pdf') {
      handlePrintPdf();
    } else if (printRequest.method === 'intent') {
      if (!isAndroidDevice()) {
        enqueueSnackbar(
          'A impressão via Android está disponível apenas em um dispositivo Android com o serviço ESC/POS instalado.',
          { variant: 'warning' }
        );
        setPrintRequest(undefined);
        return;
      }

      const didDispatch = dispatchPrintIntent(
        posPrintRef.current,
        {
          copies: printRequest.copies,
          printWidth: printRequest.printWidth,
        },
        () =>
          enqueueSnackbar(
            'Verifique se o serviço de impressão ESC/POS está instalado, ativado e configurado.',
            { variant: 'warning' }
          )
      );

      if (didDispatch) {
        setPrintRequest(undefined);
      } else {
        enqueueSnackbar('Não foi possível preparar o resumo para impressão.', {
          variant: 'error',
        });
        setPrintRequest(undefined);
      }
    } else {
      handlePrintPos();
    }
  }, [
    handlePrintPdf,
    handlePrintPos,
    printRequest,
    summaryQuery.data,
    summaryQuery.isFetching,
    enqueueSnackbar,
  ]);

  const printCashSessionSummary = (
    id: string,
    mode: CashSessionSummaryPrintMode,
    copies = 1,
    selectedLayout = PRINT_LAYOUT_1,
    printWidth = PRINT_WIDTH_80MM,
    method: CashSessionSummaryPrintMethod = 'browser'
  ) => {
    if (printRequest || summaryQuery.isFetching) return;

    setPrintRequest({
      copies: Math.max(1, copies),
      id,
      method,
      mode,
      printWidth,
      selectedLayout,
    });
  };

  return {
    isPrintingSummary: !!printRequest || summaryQuery.isFetching,
    printCashSessionSummary,
    printSummaryNode: (
      <div style={{ display: 'none' }}>
        <CashSessionSummaryPdfPrint
          summary={summaryQuery.data}
          containerRef={pdfPrintRef}
          emittedBy={user?.name}
        />
        <CashSessionSummaryPosPrint
          summary={summaryQuery.data}
          containerRef={posPrintRef}
          selectedLayout={printRequest?.selectedLayout}
          selectedWidth={printRequest?.printWidth}
        />
      </div>
    ),
  };
}
